import { z } from 'zod';
import { chatRoleSchema, chatRoomReferenceSchema, chatRoomStatusSchema } from './chat.js';
import { sessionOriginSchema } from './versioning.js';

/**
 * What the administration of the rooms puts on the wire (chat spec §12).
 *
 * Its own file rather than more of `chat.ts`, and the split is the same one §12
 * makes: `chat.ts` holds what a PARTICIPANT is served behind a token - and is
 * careful about what it withholds from her (`roomId`, the tokens, the peer's
 * consent) - while everything here is served to an OPERATOR who already holds
 * the room to person correspondence. Two audiences, two shapes, and no reader
 * has to remember which of the two he is looking at.
 *
 * These shapes carry the room IDENTIFIER, because §12 addresses a transcript by
 * it (`/admin/entretiens/<id>`) and the screen is behind the operator door. They
 * carry NEITHER TOKEN: the two links are answered once, at the creation, by
 * `chatRoomCredentialsSchema`, and nowhere else. That is not a courtesy either -
 * a read projection that could redisplay them would be a second place to leak
 * them from, and `.strict()` below is what refuses the field rather than a
 * reviewer having to notice it.
 */

/**
 * What a room is called in a list, and it is NOT the stored status.
 *
 * §12 requires « jamais ouverte » and « invité parti » to be DISPLAYED STATES
 * rather than blanks: they are the two ways of spotting an interview that did
 * not happen, and a table showing « 0 message » tells them apart from nothing.
 * `chat_rooms.status` cannot say the first of them - `open` with no message and
 * `open` with forty are the same word - so the reading folds the status and
 * `openedAt` together, once, server-side.
 *
 * `never-opened` wins over every other value, and that ordering is the point:
 * a room an interviewer closed without a word said is an interview that did not
 * happen just as much as one nobody entered, and « Terminé » beside « 0 message »
 * would read as an interview that went fine and produced nothing.
 */
export const chatRoomAdminStateSchema = z.enum(['never-opened', 'chatting', 'closed', 'abandoned']);
export type ChatRoomAdminState = z.infer<typeof chatRoomAdminStateSchema>;

/** One row of S9, and the head of S10. */
export const chatRoomSummarySchema = z
  .object({
    /** Addressed by identifier: §12 opens `/admin/entretiens/<id>`. */
    id: z.string().min(1),
    reference: chatRoomReferenceSchema,
    /** The STORED status, kept beside the reading so the two never drift silently. */
    status: chatRoomStatusSchema,
    /** What the badge says (see above). */
    state: chatRoomAdminStateSchema,
    createdAt: z.string().datetime(),
    /** First message, so « jamais ouverte » is a state and not a blank (§12). */
    openedAt: z.string().datetime().nullable(),
    closedAt: z.string().datetime().nullable(),
    messageCount: z.number().int().nonnegative(),
    /**
     * Whole minutes, or null when there is nothing to measure.
     *
     * Null and never zero: `engine/duration.ts` holds the same line on the other
     * end of the corpus, and `durationLabel` on the screen prints a dash for the
     * absence and « < 1 min » for a measured but very short exchange. A zero
     * would collapse the two.
     */
    durationMinutes: z.number().int().nonnegative().nullable(),
    /**
     * Whether the two links have stopped resolving - DERIVED at the read (§10),
     * never a column.
     *
     * On the wire because it is the fact that makes this screen worth having:
     * « le lien expire, le transcript est conservé » is a decision an operator
     * has to be able to SEE, or the first dead link reads as lost work.
     */
    linkExpired: z.boolean(),
    /**
     * Where the room was born (vitrine.md section 8.2, V12, issue #192): `prod` on
     * the atelier for a room the synchronisation brought up from the vitrine - the
     * mark of the list, and a room whose links never open there. OPTIONAL at the
     * regime of the other fields added after the fact: absent reads `dev`.
     */
    origin: sessionOriginSchema.optional(),
  })
  .strict();
export type ChatRoomSummary = z.infer<typeof chatRoomSummarySchema>;

/** S9, whole: the rooms, most recently struck first. */
export const chatRoomListSchema = z.object({ rooms: z.array(chatRoomSummarySchema) }).strict();
export type ChatRoomList = z.infer<typeof chatRoomListSchema>;

/**
 * One line of S10, in the order it ARRIVED.
 *
 * No `clientMessageId`: it is the idempotency key of §8.1, an internal of the
 * queue, and nothing on this screen reads it.
 *
 * `lateByMs` is the whole reason this shape is not `chatMessageSchema`. §8.2
 * keeps two stamps and lets the server's order win, and a message whose two
 * stamps diverge is the ONE observable trace of a cut connection this repo
 * stores: the client queue holds a message while the socket is down and posts it
 * at the reconnection, so the gap between `composedAt` and `receivedAt` IS the
 * length of the break. Null means the two agree closely enough that there is
 * nothing to tell - see `CHAT_LATE_MESSAGE_MS`.
 */
export const chatTranscriptEntrySchema = z
  .object({
    seq: z.number().int().positive(),
    role: chatRoleSchema,
    text: z.string().min(1),
    composedAt: z.string().datetime(),
    receivedAt: z.string().datetime(),
    /** How long it waited in the queue, in milliseconds. Null when it did not. */
    lateByMs: z.number().int().positive().nullable(),
    /**
     * Arrived after the interviewer had closed the room (§11).
     *
     * Marked rather than slipped in silently: the person wrote it in good faith
     * before the end, the closure accepted it, and a transcript that hid the
     * fact would show her answering a conversation that was already over.
     */
    afterClosure: z.boolean(),
  })
  .strict();
export type ChatTranscriptEntry = z.infer<typeof chatTranscriptEntrySchema>;

/**
 * S10, whole - the head figures and the thread.
 *
 * `consentedCount` out of `participantCount` and not the two stamps: §6 keeps a
 * refusal invisible TO THE OTHER SIDE, which an operator is not - but there is
 * still nothing this screen does with the hour somebody ticked a box, and a
 * count is what the head tile of the maquette shows.
 */
export const chatRoomTranscriptSchema = z
  .object({
    room: chatRoomSummarySchema,
    consentedCount: z.number().int().nonnegative(),
    participantCount: z.number().int().nonnegative(),
    /** How many entries carry a `lateByMs` - the number of breaks the thread traces. */
    breakCount: z.number().int().nonnegative(),
    entries: z.array(chatTranscriptEntrySchema),
  })
  .strict();
export type ChatRoomTranscript = z.infer<typeof chatRoomTranscriptSchema>;

/**
 * Below which a gap between the two stamps says nothing.
 *
 * A nominal post is a round trip - milliseconds - so any threshold at all would
 * do to catch a real cut. This one is a minute, and it is chosen against the
 * OTHER failure: `composedAt` is the CLIENT's clock (§8.2 keeps it as it
 * arrived), and a browser whose clock is thirty seconds off would otherwise put
 * « composé à 21:14, arrivé à 21:16 » under every single line and drown the one
 * place the gap is the information.
 *
 * Shared rather than backend-only because the screen says the number out loud
 * when it explains what a traced break is.
 */
export const CHAT_LATE_MESSAGE_MS = 60_000;
